"use client";

import { type FormEvent, useState } from "react";
import { trackEvent } from "@/lib/analytics-events";
import { retrieveReports } from "./my-reports-api";

/**
 * Self-serve "I paid but never got my report" recovery, keyed by CONTACT.
 * The buyer enters the phone or email they used at checkout; the backend re-sends
 * the purchased-report link to that contact's own WhatsApp / email. We never show a
 * report link on screen — so this is safe without a login (only the contact's
 * owner receives the links) and the confirmation is always generic.
 */
export default function MyReportsClient() {
  const [contact, setContact] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const value = contact.trim();
    if (busy || !value) return;
    setBusy(true);
    setError("");
    setMessage("");
    trackEvent("report_retrieve_submitted", {
      contact_kind: value.includes("@") ? "email" : "phone",
    });
    try {
      const result = await retrieveReports(value);
      setMessage(
        result.message ||
          "Agar is contact par koi report linked hai, humne uske link bhej diye hain 🙏"
      );
      trackEvent("report_retrieve_success", {});
    } catch {
      setError("Abhi process nahi kar paaye. Thodi der baad dobara koshish karein.");
      trackEvent("report_retrieve_failed", {});
    }
    setBusy(false);
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-5 py-16">
      <h1 className="text-title">Apni report dobara paayein</h1>
      <p className="text-body mt-3 text-text-secondary">
        Payment ho gaya lekin report nahi mili? Wahi <strong>mobile number</strong> ya{" "}
        <strong>email</strong> daaliye jo aapne checkout par use kiya tha — hum aapki report ka
        secure link usi WhatsApp / email par turant bhej denge.
      </p>

      {message ? (
        <div
          className="text-body mt-6 rounded-xl border border-border-subtle bg-surface-brand-subtle px-4 py-4 text-text-primary"
          role="status"
        >
          {message}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-6 space-y-3">
          <label htmlFor="contact" className="sr-only">
            Mobile number ya email
          </label>
          <input
            id="contact"
            name="contact"
            type="text"
            inputMode="text"
            autoComplete="off"
            value={contact}
            onChange={(ev) => setContact(ev.target.value)}
            placeholder="Mobile number ya email"
            className="text-body w-full rounded-xl border border-border-default bg-surface-raised px-4 py-3 text-text-primary outline-none focus:border-border-interactive"
          />
          <button
            type="submit"
            disabled={busy || !contact.trim()}
            className="text-button w-full rounded-xl bg-surface-brand-strong px-4 py-3 text-text-on disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? "Bhej rahe hain..." : "Mujhe meri report bhejein"}
          </button>
          {error ? (
            <p className="text-body-small text-error" role="alert">
              {error}
            </p>
          ) : null}
        </form>
      )}

      <p className="text-body-small mt-8 text-text-muted">
        Privacy ke liye report ka link sirf usi contact par bheja jaata hai jo aap yahan daalte hain
        — screen par kabhi nahi dikhaya jaata. Koi dikkat ho to{" "}
        <a href="mailto:support@ishvaram.com" className="inline text-intent-brand underline">
          support@ishvaram.com
        </a>{" "}
        par likhein.
      </p>
    </div>
  );
}
